import { useState, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet } from 'lucide-react';
import { parseCsv, rowsToUnits } from '../../utils/csv';
import { extractErrorMessage } from '../../utils/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { unit_id: 'Asset ID', location: 'Site', lsd: 'LSD', equipment_type: 'Type', style: 'Style', make: 'Manufacturer', model: 'Model', serial_number: 'Serial #', product_pumped: 'Product', notes: 'Notes' };
const TEMPLATE = 'asset_id,site,lsd,type,style,manufacturer,model,serial,product,notes\nP-100,Red Deer Battery,04-12-048-07W5,pump,Gear Pump,Viking,LQ124A,G-7781,Produced water,';

export default function UnitsImportDialog({ open, onOpenChange, customer, onImported }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = useMemo(() => (text.trim() ? rowsToUnits(parseCsv(text)) : { units: [], fields: [], hasHeader: false }), [text]);
  const valid = parsed.units.filter((u) => u.unit_id || u.serial_number);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.readAsText(f);
    e.target.value = '';
  };

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/customers/${customer.id}/units/import`, { rows: valid });
      setResult(res.data);
      toast.success(`${res.data.added} asset${res.data.added === 1 ? '' : 's'} imported`);
      onImported?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const close = (o) => { if (!o) { setText(''); setResult(null); } onOpenChange(o); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="units-import-dialog">
        <DialogHeader>
          <DialogTitle>Import Customer Assets from CSV</DialogTitle>
          <DialogDescription>
            CSV columns: Asset ID, Site, LSD, Type, Style, Manufacturer, Model, Serial, Product, Notes. Existing Asset ID + LSD records are skipped. Legacy Unit ID headers are also accepted.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50">
              <Upload size={14} /> Upload .csv
              <input type="file" accept=".csv,.txt,text/csv" className="hidden" onChange={onFile} data-testid="units-import-file" />
            </label>
            <Button type="button" variant="ghost" size="sm" onClick={() => setText(TEMPLATE)} data-testid="units-import-template-btn">
              <FileSpreadsheet size={14} className="mr-1.5" /> Load example
            </Button>
          </div>
          <div className="space-y-1.5">
            <Label>CSV rows</Label>
            <Textarea rows={8} value={text} onChange={(e) => { setText(e.target.value); setResult(null); }} placeholder={TEMPLATE} className="font-mono text-xs" data-testid="units-import-textarea" />
          </div>
          {text.trim() && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="units-import-preview">
              <p className="font-medium text-slate-800">
                {valid.length} asset{valid.length === 1 ? '' : 's'} ready
                {parsed.units.length - valid.length > 0 && <span className="text-amber-700"> · {parsed.units.length - valid.length} row(s) missing Asset ID / Serial will be skipped</span>}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Columns: {parsed.fields.map((f) => (f ? LABELS[f] : 'ignored')).join(' · ')}{parsed.hasHeader ? ' (header detected)' : ' (no header — default order)'}
              </p>
              {valid.slice(0, 3).map((u, i) => (
                <p key={i} className="text-xs font-mono text-slate-600 mt-1 truncate">{[u.unit_id, u.location, u.lsd, u.style, u.make, u.model].filter(Boolean).join(' · ')}</p>
              ))}
            </div>
          )}
          {result && (
            <p className="text-sm text-emerald-700" data-testid="units-import-result">
              Added {result.added} · Skipped {result.skipped} duplicate{result.skipped === 1 ? '' : 's'}{result.invalid ? ` · ${result.invalid} invalid` : ''}
            </p>
          )}
          <Button type="button" onClick={run} disabled={busy || !valid.length} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="units-import-submit">
            {busy ? 'Importing...' : `Import ${valid.length} asset${valid.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
